import React, { useEffect, useRef, useState } from 'react';

import { authAPI } from '@/lib/api';

/**
 * "Sign in with Google" for the office login.
 *
 * Google Identity Services (GIS) ID-token flow: the GIS script renders
 * Google's own button; when the user picks an account, Google hands us a
 * signed OpenID Connect ID token (`credential`) in the browser — no redirect,
 * no callback URL. The parent POSTs that token to /auth/google, where the
 * backend verifies it and mints the normal Report360 session.
 *
 * The OAuth client id comes from GET /auth/google/config so the static
 * frontend never needs a rebuild when the backend's client changes.
 * REACT_APP_GOOGLE_CLIENT_ID, when set at build time, overrides it (must be
 * the same client the backend verifies against, or every login is rejected
 * with "not issued for this application").
 */

const GSI_SRC = 'https://accounts.google.com/gsi/client';
let gsiLoading = null;

const loadGsi = () => {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gsiLoading) return gsiLoading;
  gsiLoading = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GSI_SRC}"]`);
    const script = existing || document.createElement('script');
    const onLoad = () => (window.google?.accounts?.id ? resolve() : reject(new Error('GIS did not initialise')));
    const onError = () => {
      gsiLoading = null;
      reject(new Error('Failed to load Google Identity Services'));
    };
    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });
    if (!existing) {
      script.src = GSI_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
  return gsiLoading;
};

const resolveConfig = async () => {
  const buildTimeClientId = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();
  if (buildTimeClientId) return { enabled: true, client_id: buildTimeClientId };
  return authAPI.googleSsoConfig();
};

const GoogleSignInButton = ({ onCredential, disabled = false }) => {
  const slotRef = useRef(null);
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;
  // 'loading' → 'ready' | 'hidden' (SSO off on the server) | 'error'
  const [state, setState] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const config = await resolveConfig();
        if (cancelled) return;
        if (!config?.enabled || !config.client_id) {
          setState('hidden');
          return;
        }
        await loadGsi();
        if (cancelled || !slotRef.current) return;
        window.google.accounts.id.initialize({
          client_id: config.client_id,
          callback: (response) => {
            if (response?.credential) onCredentialRef.current?.(response.credential);
          },
          ux_mode: 'popup',
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        window.google.accounts.id.renderButton(slotRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: Math.min(400, Math.max(200, slotRef.current.offsetWidth || 360)),
        });
        setState('ready');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Google sign-in unavailable:', err?.message || err);
        if (!cancelled) setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === 'hidden') return null;

  return (
    <div className="mt-5 space-y-4" data-testid="google-signin">
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border/60" />
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">or</span>
        <div className="h-px flex-1 bg-border/60" />
      </div>
      <div
        className={`flex min-h-[44px] justify-center ${disabled ? 'pointer-events-none opacity-60' : ''}`}
        aria-busy={disabled || state === 'loading'}
      >
        {state === 'error' ? (
          <p className="text-xs text-muted-foreground">Google sign-in is unavailable right now.</p>
        ) : (
          <div ref={slotRef} className="w-full flex justify-center" data-testid="google-signin-button" />
        )}
      </div>
    </div>
  );
};

export default GoogleSignInButton;
